<template>
  <div class="iq-wrap">
    <div class="iq-card" :class="'iq-card--' + (inquiry.leadTier || 'none')" @click="openDetail">
      <!-- property thumbnail / package cluster -->
      <div class="iq-thumb" :class="{ 'iq-thumb--stack': covers.length > 1 }">
        <template v-if="covers.length">
          <span
            v-for="(c, i) in covers"
            :key="i"
            class="iq-thumb__img"
            :class="{ 'iq-thumb__img--empty': !c }"
            :style="c ? { backgroundImage: 'url(' + c + ')' } : {}"
          >
            <hero-icon v-if="!c" :icon="HomeIcon" :size="15" />
          </span>
        </template>
        <span v-else class="iq-thumb__img iq-thumb__img--empty">
          <hero-icon :icon="isGlobal ? MapPinIcon : HomeIcon" :size="16" />
        </span>
        <span v-if="isPackage" class="iq-thumb__count">{{ properties.length }}</span>
      </div>

      <div class="iq-body">
        <!-- row 1: identity ....... temperature + state -->
        <div class="iq-row iq-row--top">
          <div class="iq-idline">
            <span v-if="!inquiry.read" class="iq-dot" title="No leída"></span>
            <span class="iq-name">{{ inquiry.name || 'Sin nombre' }}</span>
            <span class="iq-origin" :class="'iq-origin--' + origin.key">{{ origin.label }}</span>
            <span v-if="inquiry.search" class="iq-tag">Búsqueda</span>
          </div>
          <div class="iq-meta">
            <span
              v-if="inquiry.leadTier"
              class="iq-lead"
              :class="'iq-lead--' + inquiry.leadTier"
              title="Temperatura del lead (actividad del visitante)"
            >{{ tierLabel(inquiry.leadTier) }}<template v-if="inquiry.leadScore != null"> · {{ inquiry.leadScore }}</template></span>
            <span class="iq-badge" :class="'iq-badge--' + inquiry.state">{{ states[inquiry.state] }}</span>
          </div>
        </div>

        <!-- row 2: subject ....... waiting time -->
        <div class="iq-row iq-row--subject">
          <span class="iq-subject">
            <hero-icon :icon="isPackage ? Squares2X2Icon : (isGlobal ? MapPinIcon : HomeIcon)" :size="13" />
            <span class="iq-subject__text">{{ subject }}</span>
          </span>
          <span
            class="iq-waiting"
            :class="{ 'iq-waiting--activity': activityStamp }"
            :title="fullDate"
          >
            <template v-if="activityStamp">
              <hero-icon :icon="BoltIcon" :size="12" />
              <span>actividad {{ activityStamp }}</span>
            </template>
            <template v-else>{{ waiting }}</template>
          </span>
        </div>

        <!-- row 3: contact channels ....... chat pulse + journey -->
        <div class="iq-row iq-row--foot">
          <div class="iq-chans">
            <a v-if="inquiry.email" class="iq-chan" :href="'mailto:' + inquiry.email" @click.stop>
              <hero-icon :icon="EnvelopeIcon" :size="13" /><span>{{ inquiry.email }}</span>
            </a>
            <span
              v-if="emailState"
              class="iq-mailstate"
              :class="'iq-mailstate--' + emailState.key"
              :title="emailState.hint"
            >
              <hero-icon :icon="emailState.icon" :size="12" />
              <span>{{ emailState.label }}</span>
            </span>
            <a v-if="inquiry.phone" class="iq-chan" :href="'tel:' + inquiry.phone" @click.stop>
              <hero-icon :icon="PhoneIcon" :size="13" /><span>{{ inquiry.phone }}</span>
            </a>
            <span v-if="!inquiry.email && !inquiry.phone" class="iq-chan iq-chan--muted">Sin datos de contacto</span>
          </div>
          <div class="iq-footactions">
            <span v-if="chatUnread" class="iq-chatpill" title="Mensajes de chat sin leer">
              <span class="iq-chatpill__dot"></span>{{ chatUnread }}
            </span>
            <button
              v-if="showJourney && inquiry.visitorId"
              type="button"
              class="iq-link"
              @click.stop="openJourney"
            >
              <hero-icon :icon="ClockIcon" :size="13" /><span>Recorrido</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <detail
      v-if="opened"
      ref="showDetail"
      :inquiry="inquiry"
      @reload="$emit('reload')"
    ></detail>
  </div>
</template>

<script>
import HeroIcon from "@/components/shared/HeroIcon";
import { STATE_LABELS } from "@/utils/funnel";
import EnvelopeIcon from "heroicons/24/outline/envelope.svg";
import PhoneIcon from "heroicons/24/outline/phone.svg";
import HomeIcon from "heroicons/24/outline/home.svg";
import MapPinIcon from "heroicons/24/outline/map-pin.svg";
import ClockIcon from "heroicons/24/outline/clock.svg";
import BoltIcon from "heroicons/24/outline/bolt.svg";
import CheckIcon from "heroicons/24/outline/check.svg";
import EnvelopeOpenIcon from "heroicons/24/outline/envelope-open.svg";
import ExclamationTriangleIcon from "heroicons/24/outline/exclamation-triangle.svg";
import Squares2X2Icon from "heroicons/24/outline/squares-2x2.svg";

const Detail = () => import("@/components/inquiries/Detail");

export default {
  data: () => ({
    EnvelopeIcon,
    PhoneIcon,
    HomeIcon,
    MapPinIcon,
    ClockIcon,
    BoltIcon,
    CheckIcon,
    EnvelopeOpenIcon,
    ExclamationTriangleIcon,
    Squares2X2Icon,
    opened: false,
    states: STATE_LABELS,
    tierLabels: { hot: "Caliente", warm: "Tibio", cold: "Frío" }
  }),

  props: {
    inquiry: {
      required: true,
      type: Object
    },

    // show the "ver recorrido" action (hosts that wire @open-journey opt in)
    showJourney: {
      type: Boolean,
      default: false
    },

    // active list ordering — the card's timestamp mirrors the sort key so the
    // list order is legible ("latest_activity" shows activity recency, not age)
    order: {
      type: String,
      required: false,
      default: null
    }
  },

  computed: {
    // the full package (multi-property) — falls back to the primary property
    properties() {
      const list = this.inquiry.properties || [];
      if (list.length) return list;
      return this.inquiry.property ? [this.inquiry.property] : [];
    },

    isPackage() {
      return this.properties.length > 1;
    },

    isGlobal() {
      return this.properties.length === 0;
    },

    // up to 3 stacked cover thumbnails (null = property without an image)
    covers() {
      return this.properties.slice(0, 3).map(p => {
        const img = (p.images || [])[0];
        const path = img && (img.smallUrl || img.thumbUrl || img.url);
        return path ? process.env.VUE_APP_RAILS_URL + path : null;
      });
    },

    origin() {
      if (this.inquiry.manual) return { key: "manual", label: "Manual" };
      if (this.inquiry.agentConversation) return { key: "chat", label: "Chat" };
      return { key: "web", label: "Web" };
    },

    // reception state of the last email we sent this client (SendGrid webhook).
    // Collapsed to the three states an agent acts on: reached / opened / failed —
    // a bare "enviado" isn't worth the noise, so it stays hidden.
    emailState() {
      const at = this.inquiry.emailStatusAt
        ? " · " + this.timeAgo(this.inquiry.emailStatusAt)
        : "";
      switch (this.inquiry.emailStatus) {
        case "opened":
          return {
            key: "opened",
            label: "Email leído",
            icon: this.EnvelopeOpenIcon,
            hint: "El cliente abrió el email" + at
          };
        case "delivered":
          return {
            key: "delivered",
            label: "Email entregado",
            icon: this.CheckIcon,
            hint: "Entregado, sin abrir todavía" + at
          };
        case "bounced":
        case "dropped":
          return {
            key: "failed",
            label: "Email rebotado",
            icon: this.ExclamationTriangleIcon,
            hint: "No se pudo entregar — revisá la dirección" + at
          };
        default:
          return null;
      }
    },

    chatUnread() {
      const c = this.inquiry.agentConversation;
      return (c && c.agentUnread) || 0;
    },

    waiting() {
      return this.timeAgo(this.inquiry.createdAtTime || this.inquiry.createdAt);
    },

    // when the list is sorted by latest activity, surface the activity recency —
    // otherwise an old-but-bumped inquiry at the top reads as out of order.
    // Null when there's been no activity since creation (or another sort is on).
    activityStamp() {
      if (this.order !== "latest_activity") return null;
      const act = this.inquiry.lastActivityAt;
      const created = this.inquiry.createdAtTime || this.inquiry.createdAt;
      if (!act || !created) return null;
      if (new Date(act) - new Date(created) < 60000) return null;
      return this.timeAgo(act);
    },

    fullDate() {
      const created = this.inquiry.createdAtTime || this.inquiry.createdAt;
      const act = this.inquiry.lastActivityAt;
      let out = created ? "Creada: " + this.fmtDate(created) : "";
      if (act && created && new Date(act) - new Date(created) >= 60000) {
        out += "\nÚltima actividad: " + this.fmtDate(act);
      }
      return out;
    },

    subject() {
      if (this.isPackage)
        return `Paquete · ${this.properties.length} propiedades`;

      const p = this.properties[0];
      if (p) return p.fullAddress;

      const base =
        this.inquiry.topic ||
        (this.inquiry.investmentType
          ? this.$t("investmentTypes." + this.inquiry.investmentType)
          : "");
      const loc = this.inquiry.location;
      if (base && loc) return base + " · " + loc;
      return base || loc || "Consulta general";
    }
  },

  methods: {
    // Mount the detail dialog only on first open so its Comments/recommendations
    // queries never fire for unopened rows on the list.
    openDetail() {
      this.opened = true;
      // macrotask, not $nextTick: the drawer mounts DURING this click's dispatch,
      // so its click-outside handler would catch this same click bubbling to
      // document and close it instantly (the "needs two clicks" bug)
      setTimeout(() => {
        if (this.$refs.showDetail) this.$refs.showDetail.show = true;
      }, 0);
    },

    // open the visitor's full journey (the host owns the journey dialog)
    openJourney() {
      this.$emit("open-journey", this.inquiry.visitorId);
    },

    tierLabel(t) {
      return this.tierLabels[t] || t;
    },

    fmtDate(iso) {
      return new Date(iso).toLocaleString("es-AR", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    // relative "waiting" time — speed-to-lead matters more than a static date
    timeAgo(iso) {
      if (!iso) return "";
      const diff = Math.max(0, Date.now() - new Date(iso).getTime());
      const m = Math.floor(diff / 60000);
      if (m < 1) return "recién";
      if (m < 60) return `hace ${m} min`;
      const h = Math.floor(m / 60);
      if (h < 24) return `hace ${h} h`;
      const d = Math.floor(h / 24);
      if (d < 30) return `hace ${d} día${d === 1 ? "" : "s"}`;
      const mo = Math.floor(d / 30);
      if (mo < 12) return `hace ${mo} mes${mo === 1 ? "" : "es"}`;
      const y = Math.floor(d / 365);
      return `hace ${y} año${y === 1 ? "" : "s"}`;
    }
  },

  components: {
    HeroIcon,
    Detail
  }
};
</script>

<style scoped>
.iq-wrap { position: relative; }

.iq-card {
  position: relative;
  display: flex;
  gap: 14px;
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 12px;
  padding: 14px 16px 14px 18px;
  margin-bottom: 10px;
  cursor: pointer;
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.iq-card:hover {
  border-color: #dcd4c6;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18);
}
/* lead-temperature spine — a subtle heat map down the list */
.iq-card::before {
  content: "";
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 3px;
  background: transparent;
}
.iq-card--hot::before { background: #b52118; }
.iq-card--warm::before { background: #e0a43b; }
.iq-card--cold::before { background: #cfc7b8; }

/* thumbnail / package cluster */
.iq-thumb {
  position: relative;
  flex: none;
  width: 46px;
  height: 46px;
}
.iq-thumb__img {
  position: absolute;
  inset: 0;
  width: 46px;
  height: 46px;
  border-radius: 9px;
  background: #ece7de center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a39a8c;
  box-shadow: 0 0 0 2px #fff;
}
.iq-thumb--stack { width: 58px; }
/* fan the stack: base sits back, top sits front-left */
.iq-thumb--stack .iq-thumb__img:nth-child(1) { transform: translate(10px, 0); z-index: 1; }
.iq-thumb--stack .iq-thumb__img:nth-child(2) { transform: translate(5px, 0); z-index: 2; }
.iq-thumb--stack .iq-thumb__img:nth-child(3) { transform: translate(0, 0); z-index: 3; }
.iq-thumb__count {
  position: absolute;
  bottom: -4px; right: -4px;
  z-index: 5;
  min-width: 18px; height: 18px;
  padding: 0 4px;
  border-radius: 999px;
  background: #1a1814;
  color: #fff;
  font-size: 10.5px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #fff;
}

.iq-body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.iq-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }

/* row 1 */
.iq-idline { flex: 1; display: flex; align-items: center; gap: 8px; min-width: 0; }
.iq-dot { flex: none; width: 7px; height: 7px; border-radius: 50%; background: #b52118; }
.iq-name {
  font-size: 15px;
  font-weight: 600;
  color: #1a1814;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.iq-origin {
  flex: none;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 2px 7px;
}
.iq-origin--manual { background: #ece9e3; color: #6a645a; }
.iq-origin--chat { background: #e1ecff; color: #2353b8; }
.iq-origin--web { background: #f1ece3; color: #8a7d63; }
.iq-tag {
  flex: none;
  font-size: 10.5px;
  font-weight: 600;
  color: #6a645a;
  background: #f1ece3;
  border-radius: 999px;
  padding: 2px 9px;
  white-space: nowrap;
}
.iq-meta { flex: none; display: flex; align-items: center; gap: 8px; }
.iq-lead { font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 999px; text-transform: uppercase; letter-spacing: .03em; white-space: nowrap; }
.iq-lead--hot { background: #fdecea; color: #b52118; }
.iq-lead--warm { background: #fdeecf; color: #8a5a00; }
.iq-lead--cold { background: #eef1f4; color: #6a727b; }
.iq-badge { font-size: 11px; font-weight: 600; padding: 2px 10px; border-radius: 999px; line-height: 1.6; white-space: nowrap; }
.iq-badge--pending { background: #fdeecf; color: #8a5a00; }
.iq-badge--ongoing { background: #e1ecff; color: #2353b8; }
.iq-badge--on_hold { background: #ece9e3; color: #6a645a; }
.iq-badge--watching { background: #efe3f7; color: #6a3a8f; }
.iq-badge--visit { background: #e0f4f8; color: #0e7490; }
.iq-badge--negotiation { background: #fdeed3; color: #92400e; }
.iq-badge--success { background: #e3f3e8; color: #2c6e49; }
.iq-badge--lost { background: #ece9e3; color: #6a645a; }

/* row 2 */
.iq-subject { display: flex; align-items: center; gap: 7px; min-width: 0; }
.iq-subject .hero-icon { color: #a39a8c; flex: none; }
.iq-subject__text {
  font-size: 13px;
  color: #56504a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.iq-waiting { flex: none; font-size: 12px; color: #a39a8c; white-space: nowrap; }
.iq-waiting--activity {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #8a5a00;
  font-weight: 600;
}
.iq-waiting--activity .hero-icon { color: #e0a43b; }

/* row 3 */
.iq-chans { flex: 1; display: flex; flex-wrap: wrap; gap: 4px 16px; min-width: 0; }
.iq-chan {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  font-size: 12.5px;
  color: #756f64;
  text-decoration: none;
  transition: color 0.15s ease;
}
.iq-chan span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.iq-chan .hero-icon { color: #a39a8c; flex: none; }
.iq-chan:hover { color: #b52118; }
.iq-chan:hover .hero-icon { color: #b52118; }
.iq-chan--muted { color: #b6ab99; font-style: italic; }

/* email reception state (SendGrid) */
.iq-mailstate {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 600;
  border-radius: 999px;
  padding: 2px 9px;
  white-space: nowrap;
}
.iq-mailstate .hero-icon { flex: none; }
.iq-mailstate--opened { background: #e3f3e8; color: #2c6e49; }
.iq-mailstate--delivered { background: #eef1f4; color: #6a727b; }
.iq-mailstate--failed { background: #fdecea; color: #b52118; }

.iq-footactions { flex: none; display: flex; align-items: center; gap: 12px; }
.iq-chatpill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 700;
  color: #2353b8;
  background: #e1ecff;
  border-radius: 999px;
  padding: 2px 9px;
}
.iq-chatpill__dot {
  width: 6px; height: 6px; border-radius: 50%; background: #2353b8;
  animation: iq-pulse 1.8s infinite;
}
@keyframes iq-pulse {
  0% { box-shadow: 0 0 0 0 rgba(35, 83, 184, 0.5); }
  70% { box-shadow: 0 0 0 5px rgba(35, 83, 184, 0); }
  100% { box-shadow: 0 0 0 0 rgba(35, 83, 184, 0); }
}
@media (prefers-reduced-motion: reduce) { .iq-chatpill__dot { animation: none; } }
.iq-link {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  color: #6a645a;
  white-space: nowrap;
}
.iq-link:hover { color: #1a1814; text-decoration: underline; }
.iq-link .hero-icon { color: inherit; }

@media (max-width: 560px) {
  .iq-row--top { flex-wrap: wrap; }
  .iq-meta { width: 100%; justify-content: flex-start; }
}
</style>
